import classNames from 'classnames';
import React, {useMemo} from 'react';
import './styles.css';
import SelectPaymentMethodDialog from '../../scenes/Dialog/components/SelectPaymentMethodDialog';
import AmericanExpressIcon from '../icons/AmericanExpressIcon';
import BankCardIcon from '../icons/BankCardIcon';
import BankIcon from '../icons/BankIcon';
import CashAppIcon from '../icons/CashAppIcon';
import CrossIcon from '../icons/CrossIcon';
import DiscoverIcon from '../icons/DiscoverIcon';
import DotsIcon from '../icons/DotsIcon';
import MasterCardIcon from '../icons/MasterCardIcon';
import VisaCardIcon from '../icons/VisaCardIcon';

const paymentIcons = {
  card: BankCardIcon,
  us_bank_account: BankIcon,
  cashapp: CashAppIcon,
  cards: {
    visa: VisaCardIcon,
    mastercard: MasterCardIcon,
    amex: AmericanExpressIcon,
    discover: DiscoverIcon,
  }
}

const PaymentMethod = ({
  data,
  isDefault,
  extended = true,
  editable = false,
  selected,
  onSelect,
  onEdit,
  onDelete,
  onMakeDefault,
  canDelete = false,
  isLast,
}) => {
  const method = useMemo(() => {
    const result = {
      icon: BankCardIcon,
      title: '',
      number: '',
      expires: '',
    };

    if (data.type === 'card') {
      result.icon = paymentIcons.cards[data.card.brand] || paymentIcons.card;
      result.title = `${data.card.display_brand}`;
      result.number = `**** ${data.card.last4}`;
      result.expires = `Expires ${data.card.exp_month}/${data.card.exp_year}`;
    }

    if (data.type === 'us_bank_account') {
      result.icon = paymentIcons.us_bank_account;
      result.title = 'Bank Account';
      result.number = `**** ${data.us_bank_account.last4}`;
    }

    if (data.type === 'cashapp') {
      result.icon = paymentIcons.cashapp;
      result.title = 'Cash App Pay';
    }

    return result;
  }, [data]);

  const actions = useMemo(() => {
    if (!canDelete) {
      return [];
    }

    const result = [
      {
        label: 'Delete',
        onClick: () => {
          if (onDelete) {
            onDelete(data.id);
          }
        },
      }
    ];

    if (!isDefault) {
      result.push({
        label: 'Make default',
        onClick: () => {
          if (onMakeDefault) {
            onMakeDefault(data.id);
          }
        }
      });
    }

    return result;
  }, [canDelete, isDefault]);

  if (!data) {
    return null;
  }

  return (
    <tr className={classNames('bank-card', {'actions--top': isLast})} onClick={() => {
      if (onSelect) {
        onSelect(data.id);
      }
    }}>
      {!!onSelect && (
        <td className={'bank-card__select'}>
          <input
            type={'radio'}
            name={'payment-method'}
            value={data.id}
            checked={selected === data.id}
            onChange={() => onSelect(data.id)}
          />
        </td>
      )}
      <td className={'bank-card__icon'}>
        <method.icon size={20} />
      </td>
      <td className={'bank-card__title'}>
        {method.title}

        <div className={'bank-card__mobile'}>
          {method.number && (
            <div className={'bank-card__number'}>
              {method.number}
            </div>
          )}
          {extended && (
            <>
              {isDefault && (
                <div className={'bank-card__default'}>
                  <span>Default</span>
                </div>
              )}
              {method.expires && (
                <div className={'bank-card__expires'}>
                  {method.expires}
                </div>
              )}
            </>
          )}
        </div>
      </td>
      <td className={'bank-card__number'}>
        {method.number}
      </td>
      {extended && (
        <>
          <td className={'bank-card__default'}>
            {isDefault && (
              <span>Default</span>
            )}
          </td>
          <td className={'bank-card__expires'}>
            {method.expires}
          </td>
        </>
      )}
      {editable && (
        <td className={'bank-card__edit'}>
          <button onClick={() => {
            SelectPaymentMethodDialog({ method: data }).then((id) => {
              if (id) {
                onEdit(id);
              }
            });
          }}>Edit</button>
        </td>
      )}
      {canDelete && (
        <td className={'bank-card__actions-col'}>
          <div className={'bank-card__actions'}>
            {canDelete && isDefault ? (
              <button className={'bank-card__actions-trigger'} disabled={true}>
                <CrossIcon size={10} />
              </button>
            ) : (
              <>
                <button className={'bank-card__actions-trigger'}>
                  <DotsIcon size={10} />
                </button>
                <div className={'bank-card__actions-list'}>
                  {actions.map((action, index) => (
                    <button className={'bank-card__action'} key={index} onClick={action.onClick}>{action.label}</button>
                  ))}
                </div>
              </>
            )}
          </div>
        </td>
      )}
    </tr>
  );
};

export default PaymentMethod;
